import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Save, Settings as SettingsIcon, ArrowLeft, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminSettings() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    stripe_public_key: "",
    paypal_client_id: "",
    merch_shop_url: "",
    merch_shop_name: "",
    youtube_url: "",
    facebook_url: "",
    email_from: "",
    smtp_host: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { navigate("/admin/login"); return; }
    api.get("/admin/settings").then((r) => setForm({ ...form, ...r.data })).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [user, authLoading]);

  const save = async () => {
    setSaving(true);
    try {
      // Don't re-send masked secret fields (they start with "••••")
      const payload = { ...form };
      ["mailchimp_api_key", "paypal_client_id"].forEach((k) => {
        if (typeof payload[k] === "string" && payload[k].startsWith("••••")) delete payload[k];
      });
      await api.put("/admin/settings", payload);
      toast.success("Settings saved");
      const { data } = await api.get("/admin/settings");
      setForm((f) => ({ ...f, ...data }));
    }
    catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (authLoading || loading) return <div className="max-w-5xl mx-auto px-6 py-20"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16" data-testid="admin-settings">
      <Link to="/admin" className="inline-flex items-center gap-2 text-neutral-400 hover:text-[#D4AF37] text-sm mb-6">
        <ArrowLeft size={14} /> Back to Dashboard
      </Link>
      <div className="label-eyebrow mb-3 flex items-center gap-2"><SettingsIcon size={14} /> Integration Settings</div>
      <h1 className="font-serif-display text-5xl text-white">Configure <span className="italic text-[#D4AF37]">integrations</span></h1>
      <p className="text-neutral-400 mt-4">Placeholders for upcoming integrations. Production keys live in server .env.</p>

      <div className="mt-12 space-y-8">
        <Section title="CRM · Email Marketing">
          <Field label="Provider">
            <select data-testid="set-marketing-provider" value={form.marketing_provider} onChange={(e) => setForm({ ...form, marketing_provider: e.target.value })} className={inp}>
              <option value="mailchimp">Mailchimp</option>
              <option value="convertkit" disabled>ConvertKit (coming soon)</option>
              <option value="brevo" disabled>Brevo (coming soon)</option>
              <option value="activecampaign" disabled>ActiveCampaign (coming soon)</option>
            </select>
          </Field>
          <Field label="Auto-Sync on Purchase">
            <label className="flex items-center gap-2 mt-3">
              <input data-testid="set-mc-autosync" type="checkbox" checked={!!form.mailchimp_auto_sync} onChange={(e) => setForm({ ...form, mailchimp_auto_sync: e.target.checked })} />
              <span className="text-sm text-neutral-300">Enable auto-sync</span>
            </label>
          </Field>
          <Field label="Mailchimp API Key">
            <input data-testid="set-mc-key" type="text" value={form.mailchimp_api_key || ""} onChange={(e) => setForm({ ...form, mailchimp_api_key: e.target.value })} className={inp} placeholder="abcdef1234567890-us6" />
            {form.mailchimp_api_key_set && <p className="text-[10px] tracking-widest uppercase text-emerald-400 mt-1">● Key saved — type a new one to replace</p>}
          </Field>
          <Field label="Mailchimp Audience ID">
            <input data-testid="set-mc-audience" value={form.mailchimp_audience_id || ""} onChange={(e) => setForm({ ...form, mailchimp_audience_id: e.target.value })} className={inp} placeholder="a1b2c3d4e5" />
          </Field>
          <Field label="Latest Campaign URL (for Send Campaign / Send-to-Tag buttons)">
            <input data-testid="set-mc-campaign" value={form.mailchimp_campaign_url || ""} onChange={(e) => setForm({ ...form, mailchimp_campaign_url: e.target.value })} className={inp} placeholder="https://us6.admin.mailchimp.com/campaigns/?tag={tag}" />
            <p className="text-[10px] text-neutral-500 leading-relaxed mt-1">Include the literal placeholder <code className="text-[#D4AF37]">{"{tag}"}</code> to inject the tag name into the URL. If omitted, the tag is appended as <code>?tag=…</code>.</p>
          </Field>
          <Field label="Webhook URL (paste into Mailchimp audience settings)">
            <input data-testid="set-mc-webhook" readOnly value={`${process.env.REACT_APP_BACKEND_URL}/api/webhook/mailchimp`} className={inp + " text-neutral-400"} />
          </Field>
        </Section>

        <Section title="Payments">
          <Field label="Stripe Public Key" data-testid-prefix="stripe-pk">
            <input value={form.stripe_public_key} onChange={(e) => setForm({ ...form, stripe_public_key: e.target.value })} className={inp} placeholder="pk_live_..." data-testid="set-stripe-pk" />
          </Field>
          <Field label="PayPal Client ID">
            <input value={form.paypal_client_id} onChange={(e) => setForm({ ...form, paypal_client_id: e.target.value })} className={inp} placeholder="AYx..." data-testid="set-paypal" />
          </Field>
        </Section>

        <Section title="External Merchandise Store">
          <Field label="Merch Store URL (Shopify or other)">
            <input value={form.merch_shop_url || ""} onChange={(e) => setForm({ ...form, merch_shop_url: e.target.value })} className={inp} placeholder="https://www.hohvibesshop.com/" data-testid="set-merch-shop-url" />
          </Field>
          <Field label="Merch Store Display Name">
            <input value={form.merch_shop_name || ""} onChange={(e) => setForm({ ...form, merch_shop_name: e.target.value })} className={inp} placeholder="HOH_Vibes Shop" data-testid="set-merch-shop-name" />
          </Field>
          <Field label="">
            <p className="text-[11px] text-neutral-400 leading-relaxed mt-2">
              Merchandise is fulfilled externally (e.g., Shopify + Printful). The Merch page on this site acts as a gallery and links here for checkout.
            </p>
          </Field>
        </Section>

        <Section title="Social & Email">
          <Field label="YouTube Channel URL">
            <input value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })} className={inp} placeholder="https://youtube.com/@HOHVibes" data-testid="set-youtube" />
          </Field>
          <Field label="Facebook Page URL">
            <input value={form.facebook_url} onChange={(e) => setForm({ ...form, facebook_url: e.target.value })} className={inp} placeholder="https://facebook.com/hohvibes" data-testid="set-facebook" />
          </Field>
          <Field label="Email From Address">
            <input value={form.email_from} onChange={(e) => setForm({ ...form, email_from: e.target.value })} className={inp} placeholder="hanson@hohvibes.com" data-testid="set-email-from" />
          </Field>
          <Field label="SMTP Host">
            <input value={form.smtp_host} onChange={(e) => setForm({ ...form, smtp_host: e.target.value })} className={inp} placeholder="smtp.sendgrid.net" data-testid="set-smtp" />
          </Field>
        </Section>

        <div className="flex justify-end pt-4">
          <button data-testid="save-settings" onClick={save} disabled={saving} className="btn-gold px-6 py-3 rounded text-xs tracking-widest uppercase inline-flex items-center gap-2">
            <Save size={14} /> {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

const inp = "w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-3 py-2 rounded text-white text-sm";

function Section({ title, children }) {
  return (
    <div className="luxury-card rounded-xl p-6">
      <div className="label-eyebrow mb-5">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs tracking-widest uppercase text-neutral-400">{label}</label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
